<template>
  <div class="date-input-view">
    <div
      class="display-container"
      v-if="showDisplay"
    >
      <p4a-icon
        class="display-icon"
        :icon="'date'"
        :color="'#212121'"
      />
      <span
        class="display-text"
        v-text="displayText"
      >
      </span>
    </div>

    <div>
      <p4a-date-input
        class="date-input"
        :format="dateFormat"
        :value="selectedDate"
        :is-nullable="isNullable"
        :disabled="disabled"
        @change="setDate"
      >
      </p4a-date-input>

      <p4a-date-dropdown
        class="date-dropdown"
        :date="selectedDate"
        :disabled="disabled"
        @emitValue="setDate"
      />
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'
  import _ from 'lodash'
  import moment from 'moment'

  import Locales from '../../../../localization/Locales'

  export default {
    name: 'DateInputView',
    components: {
    },

    i18n: {
      messages: Locales
    },

    props: {
      dateValue: {
        default: function () {
          return moment().startOf('day').toObject()
        },
        type: Object
      },
      showDisplay: {
        default: true,
        type: Boolean
      },
      isNullable: {
        default: true,
        type: Boolean
      },
      disabled: {
        default: false,
        type: Boolean
      }
    },
    data () {
      return {
        selectedDate: {
          'date': null,
          'hours': null,
          'milliseconds': null,
          'minutes': null,
          'months': null,
          'seconds': null,
          'years': null
        }
      }
    },
    computed: {
      // authenticatedUser
      authenticatedUser () {
        return this.$store.getters.authenticatedUser
      },
      systemSettings () {
        return this.authenticatedUser.SystemSettings
      },
      dateFormat () {
        let self = this
        let dateFormat = self.systemSettings.DateFormat
        ? self.systemSettings.DateFormat
        : self.systemSettings.DateTimeFormat

        // DateFormats have to be made moment-specific (use regex global option to replace all)
        dateFormat = _.replace(dateFormat, new RegExp('d', 'g'), 'D')
        dateFormat = _.replace(dateFormat, new RegExp('y', 'g'), 'Y')

        return dateFormat
      },
      dateTimeValue () {
        let self = this
        return moment(self.selectedDate).toDate()
      },
      displayDate () {
        let self = this
        let selectedMoment = moment(self.selectedDate)

        if (self.isValidDate) {
          return selectedMoment.format(self.dateFormat)
        } else {
          return null
        }
      },
      displayText () {
        let self = this
        if (self.isValidDate) {
          return self.$t('kitComponents.dateInputView.displayText', [self.displayDate])
        } else {
          return self.$t('kitComponents.dateInputView.diplaySelectText')
        }
      },
      isValidDate () {
        let self = this
        return self.isNullable ? self.containsNull(self.selectedDate) : moment().isValid()
      }
    },
    methods: {
      segmentSetterKey (segment) {
        switch (segment) {
          case 'dd':
            return 'date'
          case 'mm':
            return 'month'
          case 'yyyy':
            return 'year'
          default:
            // segment shouldn't be anything else
            return null
        }
      },
      segmentKey (segment) {
        switch (segment) {
          case 'dd':
            return 'days'
          case 'mm':
            return 'months'
          case 'yyyy':
            return 'years'
          default:
            // segment shouldn't be anything else
            return null
        }
      },
      containsNull (dateObject) {
        let result = true
        _.forEach(dateObject, value => {
          if (value === null) {
            result = false
          }
        })
        return result
      },
      setDate (newDate) {
        let self = this
        if (_.size(newDate) === 0) {
          self.setNullAndEmit()
          return
        }
        let newMoment = moment()
        newMoment = newMoment.set({
          'year': newDate.years,
          'month': newDate.months,
          'date': newDate.date
        })
        newMoment = newMoment.startOf('date')

        Vue.set(self, 'selectedDate', newMoment.toObject())
      },
      setNull () {
        let self = this
        Vue.set(self.selectedDate, 'years', null)
        Vue.set(self.selectedDate, 'months', null)
        Vue.set(self.selectedDate, 'date', null)
      },
      setNullAndEmit () {
        let self = this
        self.setNull()
        self.emitDateTime()
      },
      emitDateTime () {
        let self = this
        let result = self.isValidDate ? self.dateTimeValue : null

        if (result || (!result && self.isNullable)) {
          self.$emit('emitValue', result)
        }
      }
    },
    watch: {
      dateValue: {
        handler: function (current, prev) {
          let self = this
          if (_.isEqual(current.years, null) &&
          _.isEqual(current.months, null) &&
          _.isEqual(current.date, null)) {
            self.setNull()
          } else {
            let currentMoment = moment(current)

            currentMoment.set({
              'year': current.years,
              'month': current.months,
              'date': current.date
            })

            let prevMoment = moment(prev).toObject()

            if (!_.isEqual(currentMoment.years, prevMoment.years) ||
            !_.isEqual(currentMoment.months, prevMoment.months) ||
            !_.isEqual(currentMoment.date, prevMoment.date)) {
              self.setDate(moment(currentMoment).toObject())
            }
          }
        },
        deep: true,
        immediate: true
      },
      selectedDate: {
        handler: function (current, prev) {
          let self = this

          if (prev && (
          !_.isEqual(current.years, prev.years) ||
          !_.isEqual(current.months, prev.months) ||
          !_.isEqual(current.date, prev.date))) {
            self.emitDateTime()
          }
        },
        deep: true,
        immediate: true
      },
      disabled: {
        handler (newValue) {
          let self = this
          if (newValue === true) {
            self.setNull()
          }
        },
        immediate: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  /*@import "~ui.kit.styling";*/
  @import '../../uikit';

  .date-input-view {
    box-sizing: border-box;
    font-size: 14px;

    .display-container {
      font-size: 0;
      padding-bottom: 10px;

      .display-icon {
        height: 25px;
        width: 25px;
      }

      .display-text {
        font-size: 14px;
        vertical-align: top;
        line-height: 25px;
        height: 25px;
        padding-left: 10px;
        color: $lightGreyFont
      }
    }

    .date-input {
      display: inline-block;
    }

    .date-dropdown {
      display: inline-block;
      width: 25px;
      vertical-align: top;
    }
  }

</style>



// WEBPACK FOOTER //
// node_modules/pro4all-ui/sns-components/kit-components/input-templates/date/DateInputView.vue